import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import { Card, Loader, ScrollableContainer, StyledPageWrapper, Title } from "@monorepo/components";
import { arrayBufferToBase64, getHistoryEntryPaidDate } from "@monorepo/helpers";
import { historyApi, useGetTransactionsSummaryByMonthQuery } from "@monorepo/services/api/history";
import theme from "@monorepo/styles/theme";
import { IAttributesSliceState, IResponseError, ITransactionEntry, PaymentOptionEnum } from "@monorepo/types";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Box, Typography } from "@mui/material";

import MonthTotalBox from "./MonthTotalBox";

export const SEntryCard = styled(Card)(() => ({
  display: "grid",
  gridTemplateColumns: "0.2fr 1fr 0.6fr",
  gridGap: "1rem",
  width: "100%",
  maxWidth: "460px",
  padding: `${theme.spacing(12 / 8)} ${theme.spacing(3)}!important`,
  "&:hover": {
    transition: "stroke 0.3s ease",
    backgroundColor: theme.palette.card.dark,
    color: theme.palette.secondary.main,
  },
}));

export const SScrollableContainer = styled(ScrollableContainer)(() => ({
  display: "grid",
  maxWidth: "460px",
  width: "100%",
  justifySelf: "center",
  gridRowGap: theme.spacing(18 / 8),
  paddingRight: "0.25rem",
}));

interface IProps {
  cartGroupId: string;
  period: string;
}

export const HistoryMonthSummary = ({ period, cartGroupId }: IProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [downloadId, setDownloadId] = useState<string>();
  const [year, month] = period.split("-");
  const attributesObject = useSelector(
    ({ attributesSlice }: IAttributesSliceState) => attributesSlice?.["attributes-object"]
  );

  const {
    data: transactionEntries,
    isLoading,
    error: entriesError,
  } = useGetTransactionsSummaryByMonthQuery({
    year,
    month,
  });

  const filteredEntries = useMemo(() => {
    return transactionEntries?.filter((e) => String(e.cart_group_id) === cartGroupId);
  }, [transactionEntries, cartGroupId]);

  const totalSum = useMemo(() => filteredEntries?.reduce((acc, cur) => acc + Number(cur.sum), 0), [filteredEntries]);
  const totalDiscount = useMemo(
    () => filteredEntries?.reduce((acc, cur) => acc + Number(cur.discount), 0),
    [filteredEntries]
  );
  const total = useMemo(() => filteredEntries?.reduce((acc, cur) => acc + Number(cur.total), 0), [filteredEntries]);

  useEffect(() => {
    if (Array.isArray(entriesError)) {
      entriesError.forEach((err) => {
        toast.error(err.title);
      });
    }
  }, [entriesError]);

  const handleDownload = useCallback(
    async (entry: ITransactionEntry) => {
      setDownloadId(entry.id);
      const result = dispatch(historyApi.endpoints.getTransactionReportById.initiate(entry.id));
      //todo dispatch type
      //@ts-ignore
      const { data, error: downloadError } = await result;
      if (!downloadError && data) {
        const fileName =
          t("payments.order_nr") +
          " " +
          (entry.payment_method === PaymentOptionEnum.CASH ? entry.barcode : entry.id) +
          ".pdf";
        if (attributesObject?.isPdfNeededForApp) {
          window.parent.postMessage(
            {
              data: {
                data: arrayBufferToBase64(data),
                fileName,
              },
              eventType: "pdf-download",
              type: "third-party",
            },
            attributesObject.parentOriginUrl
          );
        } else {
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        //todo dispatch type
        //@ts-ignore
        result.unsubscribe();
      } else if (downloadError) {
        toast.error((downloadError as Array<IResponseError>)?.[0].title);
      }
      setDownloadId("");
    },
    [dispatch, t, attributesObject]
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!isLoading) {
    return (
      <StyledPageWrapper>
        <Title>{t(`months_summary.${Number(month)}`)}</Title>
        <MonthTotalBox paidTotal={total} sum={totalSum} discount={totalDiscount} />
        <SScrollableContainer>
          {filteredEntries && (
            <>
              {filteredEntries.map((entry) => {
                const isCashPmt = entry.payment_method === PaymentOptionEnum.CASH;
                return (
                  entry && (
                    <SEntryCard
                      key={entry.id}
                      onClick={() => {
                        handleDownload(entry);
                      }}
                      disabled={!!downloadId}
                    >
                      <>{entry.id === downloadId ? <Loader /> : <DescriptionOutlinedIcon />}</>
                      <Box
                        sx={{
                          display: "grid",
                          textAlign: "left",
                          gridRowGap: theme.spacing(6 / 8),
                        }}
                      >
                        <Typography
                          fontWeight="bold"
                          variant="body2"
                          sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
                        >
                          {entry.product_name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography component="span" variant="caption">
                            {t("payments.order_nr")} {isCashPmt ? entry.barcode : entry.id}
                          </Typography>
                        </Box>
                      </Box>
                      <Box component="span" sx={{ display: "grid", textAlign: "right" }}>
                        <Typography fontWeight="bold">{entry.sum} €</Typography>
                        <Typography component="span" variant="caption">
                          {getHistoryEntryPaidDate(entry).slice(0, 10)}
                        </Typography>
                      </Box>
                    </SEntryCard>
                  )
                );
              })}
            </>
          )}
        </SScrollableContainer>
      </StyledPageWrapper>
    );
  }

  return null;
};

export default HistoryMonthSummary;
